import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import useErrorToast from "@/hooks/utils/useErrorToast";
import { cn } from "@/lib/utils";
import {
    useFocusSortMode,
    type FocusSortMode,
} from "@cs_teamwaydev/mailvista-sdk_react";
import { Check, Clock, Sparkle } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    pillToggleCompactItemClassName,
    pillToggleGroupClassName,
    pillToggleItemClassName,
    pillToggleLabelClassName,
} from "./pillToggle";

interface FocusSortToggleProps {
    /** Appelé après une bascule réussie, même si l'utilisateur a changé de liste ou de vue entre-temps. */
    onSortModeChange: () => void;
    /** Colonne étroite de la liste latérale : pleine largeur, libellés courts, « Afficher par » au survol. */
    compact?: boolean;
}

/** Bouton « Afficher par : Par pertinence / Par chronologie » du Focus */
const FocusSortToggle = ({
    onSortModeChange,
    compact,
}: FocusSortToggleProps) => {
    const { t } = useTranslation();
    const showErrorToast = useErrorToast();
    const { mode, setMode, isAvailable } = useFocusSortMode();
    const [isSwitching, setIsSwitching] = useState(false);
    const labelId = useId();
    const itemClassName = cn(
        pillToggleItemClassName,
        compact && pillToggleCompactItemClassName,
    );

    const handleChange = async (value: string) => {
        if (!value || value === mode) return;

        setIsSwitching(true);
        try {
            await setMode(value as FocusSortMode);
            onSortModeChange();
        } catch (error) {
            console.error("Error switching Focus sort mode", error);
            showErrorToast({ titleTranslationKey: "errors.focusSortMode" });
        } finally {
            setIsSwitching(false);
        }
    };

    // Seule l'analyse initiale rend le bouton indisponible durablement ; une bascule en cours, non
    const isLockedByInitialAnalysis = !isAvailable && !isSwitching;

    const toggle = (
        <div
            className="flex flex-wrap items-center gap-x-2 gap-y-1"
            data-testid="focus-sort-toggle"
            title={
                compact && !isLockedByInitialAnalysis
                    ? t("focusSort.label")
                    : undefined
            }
        >
            <span
                className={compact ? "sr-only" : pillToggleLabelClassName}
                id={labelId}
            >
                {t("focusSort.label")}
            </span>
            <ToggleGroup
                aria-labelledby={labelId}
                className={pillToggleGroupClassName}
                disabled={!isAvailable}
                onValueChange={handleChange}
                type="single"
                value={mode}
            >
                <ToggleGroupItem className={itemClassName} value="IMPORTANCE">
                    {mode === "IMPORTANCE" ? <Check /> : <Sparkle />}
                    {t(
                        compact
                            ? "focusSort.relevanceShort"
                            : "focusSort.relevance",
                    )}
                </ToggleGroupItem>
                <ToggleGroupItem className={itemClassName} value="DATE">
                    {mode === "DATE" ? <Check /> : <Clock />}
                    {t(
                        compact
                            ? "focusSort.chronologyShort"
                            : "focusSort.chronology",
                    )}
                </ToggleGroupItem>
            </ToggleGroup>
        </div>
    );

    if (!isLockedByInitialAnalysis) return toggle;

    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>{toggle}</TooltipTrigger>
                <TooltipContent>
                    {t("focusSort.unavailableDuringAnalysis")}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default FocusSortToggle;
